import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { post } from '../shared/apiClient';
import { HttpError } from '../shared/httpTypes';
import { persistFlowSession } from '../features/flow/session';
import { useAuth } from '../contexts/AuthContext';

interface InitResponse {
  session_uuid: string;
  country: string;
  tax_year: number;
  access_token: string;
}

interface InitErrorBody {
  error?: string;
  redirect_url?: string;
}

const appBaseUrl =
  (process.env as Record<string, string | undefined>).REACT_APP_APP_BASE_URL || '';

/** /entry — receives a one-time token from the App and bootstraps a flow session. */
const EntryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { applyToken } = useAuth();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const initiated = useRef(false);

  useEffect(() => {
    if (initiated.current) return;
    initiated.current = true;

    const token = searchParams.get('token');

    if (!token) {
      setErrorMsg('No entry token provided. Please return to the app and try again.');
      return;
    }

    (async () => {
      try {
        const { data } = await post<{ token: string }, InitResponse>(
          '/calc-entry/init',
          { token },
          { credentials: 'include' },
        );
        persistFlowSession(data.session_uuid, data.country, data.tax_year, 'anonymous');
        applyToken(data.access_token);
        navigate('/new', { replace: true });
      } catch (e) {
        if (e instanceof HttpError) {
          const body = e.data as InitErrorBody | undefined;
          if (body?.redirect_url) {
            window.location.href = body.redirect_url;
            return;
          }
          setErrorMsg(body?.error || e.message || 'Something went wrong. Please try again.');
        } else {
          setErrorMsg('Something went wrong. Please try again.');
        }
      }
    })();
  }, [searchParams, navigate]);

  if (errorMsg) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-4 bg-[#F9FAFB]">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white shadow-sm p-8 flex flex-col gap-4 text-center">
          <h1 className="text-lg font-semibold text-[#131315]">Unable to start</h1>
          <p className="text-sm text-[#525353]">{errorMsg}</p>
          {appBaseUrl ? (
            <a
              href={appBaseUrl}
              className="mt-2 px-4 py-2 rounded-lg bg-[#6ECFCF] text-[#131315] text-sm font-semibold hover:bg-[#069694] transition-colors"
            >
              Return to app
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <div
        className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#07A2A0]"
        aria-label="Loading"
        role="status"
      />
    </div>
  );
};

export default EntryPage;